import { MsdynamicsValueMap, MsdynamicsValueMapEntry } from "../../schema/dynamics";

/** The keys a map always lists, even without an entry, so a missing one is visible. */
const FIXED_KEYS: Partial<Record<MsdynamicsValueMap["kind"], string[]>> = {
	GENDER: ["MALE", "FEMALE"],
	// A campaign response's sentiment and channel (PREC-4367).
	SENTIMENT: ["INTERESTED", "NOT_INTERESTED", "BAD_TIMING", "WRONG_PERSON", "UNSUBSCRIBED"],
	CHANNEL: ["EMAIL", "LINKEDIN"],
	// A Dynamics campaign's status (PREC-4368).
	CAMPAIGN_STATE: ["PROPOSED", "LAUNCHED", "SUSPENDED", "COMPLETED"],
};

export interface ValueMapRow {
	sourceKey: string;
	/** What the key is written as, for display; `null` when nothing is written for it. */
	display: string | null;
	entry: MsdynamicsValueMapEntry | null;
}

/** What the target gets for one entry: an option shows its label and value, text itself. */
export function describeEntry(
	valueMap: Pick<MsdynamicsValueMap, "target">,
	entry: MsdynamicsValueMapEntry | null
): string | null {
	if (!entry || entry.targetValue === "") {
		return null;
	}

	if (valueMap.target === "TEXT") {
		return entry.targetValue;
	}

	return entry.targetLabel ? `${entry.targetLabel} (${entry.targetValue})` : entry.targetValue;
}

/** One row per key of the map (PREC-4363): the fixed keys first, then any other entry. */
export function valueMapRows(valueMap: MsdynamicsValueMap): ValueMapRow[] {
	const fixed = FIXED_KEYS[valueMap.kind] ?? [];
	const keys = [
		...fixed,
		...valueMap.entries.map((entry) => entry.sourceKey).filter((key) => !fixed.includes(key)),
	];

	return keys.map((sourceKey) => {
		const entry = valueMap.entries.find((candidate) => candidate.sourceKey === sourceKey) ?? null;

		return { sourceKey, entry, display: describeEntry(valueMap, entry) };
	});
}

/** The target options the resolver could not assign to any key. */
export function unplacedLabels(valueMap: MsdynamicsValueMap): string[] {
	return (valueMap.unplaced ?? []).map((option) => option.label);
}

/** Whether a map is keyed by a closed, short set (gender, sentiment, channel) and can be listed inline. */
export function isInlineValueMap(valueMap: Pick<MsdynamicsValueMap, "kind">): boolean {
	return Boolean(FIXED_KEYS[valueMap.kind]);
}

/** The targets a key can be pointed at: every option or record the map knows. */
export function valueMapTargets(
	valueMap: MsdynamicsValueMap,
	options: { label: string; value: string }[] = []
): { value: string; label: string }[] {
	const known = new Map<string, string>();

	for (const option of options) {
		known.set(option.value, option.label);
	}

	for (const entry of valueMap.entries) {
		if (entry.targetValue && !known.has(entry.targetValue)) {
			known.set(entry.targetValue, entry.targetLabel ?? entry.targetValue);
		}
	}

	for (const option of valueMap.unplaced ?? []) {
		known.set(option.value, option.label);
	}

	return [...known.entries()].map(([value, label]) => ({ value, label }));
}

/** Whether a user-typed key is a two-letter country code (PREC-4364). */
export function isCountryCodeInput(value: string): boolean {
	return /^[A-Za-z]{2}$/.test(value.trim());
}
